import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, Users, Building2, School, FileText, 
  AlertCircle, CheckCircle, Clock, Calendar, RefreshCw, 
  ChevronRight, Filter, Download, Eye, Trash2
} from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const CombinedSubscriptionDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    tenants: { total: 0, active: 0, blocked: 0, pending_amount: 0, month_collection: 0 },
    groups: { total: 0, active: 0, blocked: 0, pending_amount: 0, month_collection: 0 }
  });
  const [pendingInvoices, setPendingInvoices] = useState({ tenants: [], groups: [] });
  const [paidInvoices, setPaidInvoices] = useState({ tenants: [], groups: [] });
  const [recentPayments, setRecentPayments] = useState([]);
  const [filter, setFilter] = useState('all'); // all, tenants, groups
  const [activeTab, setActiveTab] = useState('pending'); // pending, paid

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch both tenant and group subscription data
      const [tenantReport, groupReport, tenantInvoices, groupInvoices, tenantPaidInvoices, groupPaidInvoices] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/report`, { headers }).catch(() => ({ data: { summary: {} } })),
        axios.get(`${API_URL}/api/groups/subscription-report`, { headers }).catch(() => ({ data: { summary: {} } })),
        axios.get(`${API_URL}/api/subscriptions/all-invoices?status=pending`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API_URL}/api/groups/all-invoices?status=pending`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API_URL}/api/subscriptions/all-invoices?status=paid`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API_URL}/api/groups/all-invoices?status=paid`, { headers }).catch(() => ({ data: { invoices: [] } }))
      ]);

      const tenantSummary = tenantReport.data?.summary || {};
      const groupSummary = groupReport.data?.summary || {};

      setStats({
        tenants: {
          total: tenantSummary.total_tenants || 0,
          active: tenantSummary.active_tenants || 0,
          blocked: tenantSummary.blocked_tenants || 0,
          pending_amount: tenantSummary.total_pending_amount || 0,
          month_collection: tenantSummary.month_collection || 0
        },
        groups: {
          total: groupSummary.total_groups || 0,
          active: groupSummary.active_groups || 0,
          blocked: groupSummary.blocked_groups || 0,
          pending_amount: groupSummary.total_pending_amount || 0,
          month_collection: groupSummary.month_collection || 0
        }
      });

      // Combine and sort pending invoices
      const tInvoices = (tenantInvoices.data?.invoices || []).map(inv => ({ ...inv, type: 'tenant' }));
      const gInvoices = (groupInvoices.data?.invoices || []).map(inv => ({ ...inv, type: 'group' }));
      
      setPendingInvoices({
        tenants: tInvoices,
        groups: gInvoices
      });

      // Set paid invoices
      const tPaidInvoices = (tenantPaidInvoices.data?.invoices || []).map(inv => ({ ...inv, type: 'tenant' }));
      const gPaidInvoices = (groupPaidInvoices.data?.invoices || []).map(inv => ({ ...inv, type: 'group' }));
      
      setPaidInvoices({
        tenants: tPaidInvoices,
        groups: gPaidInvoices
      });

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Handle marking invoice as paid
  const handleMarkPaid = async (invoice) => {
    if (!window.confirm(`Mark invoice ${invoice.invoice_number} as paid?\n\nAmount: ₹${invoice.amount?.toLocaleString('en-IN')}\nThis will extend the subscription.`)) {
      return;
    }
    
    try {
      const endpoint = invoice.type === 'group' 
        ? `${API_URL}/api/groups/invoice/${invoice.id}/mark-paid`
        : `${API_URL}/api/subscriptions/mark-invoice-paid/${invoice.id}`;
      
      const res = await axios.post(endpoint, {}, { headers });
      
      alert(`✅ ${res.data.message}\n\nNew End Date: ${res.data.new_end_date}\nDays Added: ${res.data.days_added}`);
      fetchData(); // Refresh data
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark invoice as paid');
    }
  };

  // Handle deleting invoice
  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`⚠️ Delete invoice ${invoice.invoice_number}?\n\nAmount: ₹${invoice.amount?.toLocaleString('en-IN')}\nFor: ${invoice.tenant_name || invoice.group_name}\n\nThis action cannot be undone!`)) {
      return;
    }
    
    try {
      const endpoint = invoice.type === 'group' 
        ? `${API_URL}/api/groups/invoice/${invoice.id}`
        : `${API_URL}/api/subscriptions/invoice/${invoice.id}`;
      
      await axios.delete(endpoint, { headers });
      
      alert(`✅ Invoice ${invoice.invoice_number} deleted successfully`);
      fetchData(); // Refresh data
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const totalStats = {
    total: stats.tenants.total + stats.groups.total,
    active: stats.tenants.active + stats.groups.active,
    blocked: stats.tenants.blocked + stats.groups.blocked,
    pending_amount: stats.tenants.pending_amount + stats.groups.pending_amount,
    month_collection: stats.tenants.month_collection + stats.groups.month_collection
  };

  const allPendingInvoices = [...pendingInvoices.tenants, ...pendingInvoices.groups]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const allPaidInvoices = [...paidInvoices.tenants, ...paidInvoices.groups]
    .sort((a, b) => new Date(b.payment_date || b.created_at) - new Date(a.payment_date || a.created_at));

  const filteredInvoices = filter === 'all' 
    ? allPendingInvoices 
    : filter === 'tenants' 
      ? pendingInvoices.tenants 
      : pendingInvoices.groups;

  const filteredPaidInvoices = filter === 'all' 
    ? allPaidInvoices 
    : filter === 'tenants' 
      ? paidInvoices.tenants 
      : paidInvoices.groups;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="combined-subscription-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription Dashboard</h1>
          <p className="text-gray-500">Tenants + Groups combined overview</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Combined Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Subscribers</p>
              <p className="text-3xl font-bold">{totalStats.total}</p>
              <p className="text-xs text-blue-200 mt-1">
                {stats.tenants.total} Tenants + {stats.groups.total} Groups
              </p>
            </div>
            <Users className="h-10 w-10 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Active</p>
              <p className="text-3xl font-bold">{totalStats.active}</p>
              <p className="text-xs text-green-200 mt-1">
                {stats.tenants.active} T + {stats.groups.active} G
              </p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Blocked</p>
              <p className="text-3xl font-bold">{totalStats.blocked}</p>
              <p className="text-xs text-red-200 mt-1">
                {stats.tenants.blocked} T + {stats.groups.blocked} G
              </p>
            </div>
            <AlertCircle className="h-10 w-10 text-red-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Pending Amount</p>
              <p className="text-2xl font-bold">{formatCurrency(totalStats.pending_amount)}</p>
              <p className="text-xs text-orange-200 mt-1">
                {pendingInvoices.tenants.length + pendingInvoices.groups.length} invoices
              </p>
            </div>
            <Clock className="h-10 w-10 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-5 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">This Month</p>
              <p className="text-2xl font-bold">{formatCurrency(totalStats.month_collection)}</p>
              <p className="text-xs text-purple-200 mt-1">Collection</p>
            </div>
            <TrendingUp className="h-10 w-10 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Split Stats - Tenants vs Groups */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tenants Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6" />
              <div>
                <h3 className="font-bold">Individual Tenants</h3>
                <p className="text-sm text-blue-100">Single college subscriptions</p>
              </div>
            </div>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-800">{stats.tenants.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.tenants.active}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats.tenants.blocked}</p>
              <p className="text-xs text-gray-500">Blocked</p>
            </div>
          </div>
          <div className="px-4 pb-4 grid grid-cols-2 gap-4">
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(stats.tenants.pending_amount)}</p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(stats.tenants.month_collection)}</p>
            </div>
          </div>
        </div>

        {/* Groups Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4">
            <div className="flex items-center gap-3">
              <School className="h-6 w-6" />
              <div>
                <h3 className="font-bold">Group of Colleges</h3>
                <p className="text-sm text-purple-100">Multi-college subscriptions</p>
              </div>
            </div>
          </div>
          <div className="p-4 grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-800">{stats.groups.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.groups.active}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats.groups.blocked}</p>
              <p className="text-xs text-gray-500">Blocked</p>
            </div>
          </div>
          <div className="px-4 pb-4 grid grid-cols-2 gap-4">
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(stats.groups.pending_amount)}</p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(stats.groups.month_collection)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Section with Tabs */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'pending' 
                  ? 'border-orange-500 text-orange-600 bg-orange-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Clock className="h-4 w-4" />
              Pending Invoices
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'pending' ? 'bg-orange-200 text-orange-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {allPendingInvoices.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('paid')}
              className={`px-6 py-4 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'paid' 
                  ? 'border-green-500 text-green-600 bg-green-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckCircle className="h-4 w-4" />
              Payment History
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'paid' ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-600'
              }`}>
                {allPaidInvoices.length}
              </span>
            </button>
          </div>
        </div>

        {/* Tab Content Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            {activeTab === 'pending' ? (
              <FileText className="h-5 w-5 text-orange-600" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-600" />
            )}
            <h2 className="font-bold text-lg">
              {activeTab === 'pending' ? 'Pending Invoices' : 'Payment History'}
            </h2>
          </div>
          
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-1 border rounded-lg text-sm"
            >
              <option value="all">All ({activeTab === 'pending' ? allPendingInvoices.length : allPaidInvoices.length})</option>
              <option value="tenants">Tenants ({activeTab === 'pending' ? pendingInvoices.tenants.length : paidInvoices.tenants.length})</option>
              <option value="groups">Groups ({activeTab === 'pending' ? pendingInvoices.groups.length : paidInvoices.groups.length})</option>
            </select>
          </div>
        </div>

        {/* Pending Tab Content */}
        {activeTab === 'pending' && (
          <>
            {filteredInvoices.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                <p className="font-medium">No pending invoices!</p>
                <p className="text-sm">All invoices have been paid.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredInvoices.slice(0, 20).map((invoice) => {
                      const isOverdue = new Date(invoice.due_date) < new Date();
                      return (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                              invoice.type === 'tenant' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {invoice.type === 'tenant' ? <Building2 className="h-3 w-3" /> : <School className="h-3 w-3" />}
                              {invoice.type === 'tenant' ? 'Tenant' : 'Group'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{invoice.invoice_number}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm">{invoice.tenant_name || invoice.group_name || 'N/A'}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm capitalize">{invoice.plan_type || 'N/A'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-bold">{formatCurrency(invoice.amount)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                              {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                            </p>
                            {isOverdue && <span className="text-xs text-red-500">Overdue</span>}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              isOverdue ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {isOverdue ? 'Overdue' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleMarkPaid(invoice)}
                                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                              >
                                <CheckCircle className="h-3.5 w-3.5" />
                                Mark Paid
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(invoice)}
                                className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center gap-1"
                                title="Delete Invoice"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {filteredInvoices.length > 20 && (
              <div className="p-4 border-t text-center">
                <p className="text-sm text-gray-500">
                  Showing 20 of {filteredInvoices.length} invoices
                </p>
              </div>
            )}
          </>
        )}

        {/* Paid/Payment History Tab Content */}
        {activeTab === 'paid' && (
          <>
            {filteredPaidInvoices.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No payment history yet</p>
                <p className="text-sm">Paid invoices will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredPaidInvoices.slice(0, 50).map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            invoice.type === 'tenant' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {invoice.type === 'tenant' ? <Building2 className="h-3 w-3" /> : <School className="h-3 w-3" />}
                            {invoice.type === 'tenant' ? 'Tenant' : 'Group'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-sm">{invoice.invoice_number}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{invoice.tenant_name || invoice.group_name || 'N/A'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm capitalize">{invoice.plan_type || 'N/A'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-green-600">{formatCurrency(invoice.amount)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">
                            {invoice.payment_date 
                              ? formatDate(invoice.payment_date)
                              : 'N/A'
                            }
                          </p>
                          {invoice.marked_paid_by && (
                            <p className="text-xs text-gray-400">by {invoice.marked_paid_by}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                            Paid
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {filteredPaidInvoices.length > 50 && (
              <div className="p-4 border-t text-center">
                <p className="text-sm text-gray-500">
                  Showing 50 of {filteredPaidInvoices.length} payments
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a
          href="/superadmin/tenant-management"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">Manage Tenants</p>
              <p className="text-sm text-gray-500">View all tenants</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </a>

        <a
          href="/superadmin/groups"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <School className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium">Manage Groups</p>
              <p className="text-sm text-gray-500">View all groups</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </a>

        <a
          href="/superadmin/subscription-plans"
          className="flex items-center justify-between p-4 bg-white rounded-xl shadow hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium">Subscription Plans</p>
              <p className="text-sm text-gray-500">Manage pricing</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </a>
      </div>
    </div>
  );
};

export default CombinedSubscriptionDashboard;
